import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from "react-apexcharts";
import { useMemo } from "react";
import Icon from "components/Icon/Icon";
import fr from "apexcharts/dist/locales/fr.json";

export default function UserStatistics({ data }: { data: any }) {
  const { t } = useTranslation();

  const userStats = useMemo(
    () => [
      {
        name: t("stats.totalRegistrations"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data?.totals?.registrations || 0,
      },
      {
        name: t("stats.totalWebLogins"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data?.totals?.webLogins || 0,
      },
      {
        name: t("stats.totalIosLogins"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data?.totals?.iosLogins || 0,
      },
      {
        name: t("stats.totalAndroidLogins"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data?.totals?.androidLogins || 0,
      },
      {
        name: t("stats.totalLogins"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data?.totals?.totalLogins || 0,
      },
    ],
    [data, t]
  );

  // Registrations data - same format as postsPerSite
  const registrationsDataset = {
    labels: data?.registrations?.map(
      (item: { date: string; value: number }) => item.date
    ),
    datasets: [
      {
        name: t("stats.registrations"),
        data:
          data?.registrations?.map(
            (item: { date: string; value: number }) => item.value
          ) ?? [],
      },
    ],
  };

  // Logins data - same format as threadsPerType
  const loginsByPlatform = {
    datasets:
      data?.logins && data.logins.length > 0
        ? [
            {
              name: t("stats.webLogins"),
              data: data.logins.map((item: any) => ({
                x: item.date,
                y: item.web || 0,
              })),
              borderWidth: 1,
            },
            {
              name: t("stats.iosLogins"),
              data: data.logins.map((item: any) => ({
                x: item.date,
                y: item.ios || 0,
              })),
              borderWidth: 1,
            },
            {
              name: t("stats.androidLogins"),
              data: data.logins.map((item: any) => ({
                x: item.date,
                y: item.android || 0,
              })),
              borderWidth: 1,
            },
            {
              name: t("stats.totalLogins"),
              data: data.logins.map((item: any) => ({
                x: item.date,
                y: item.total || 0,
              })),
              borderWidth: 1,
            },
          ]
        : [],
  };

  return (
    <>
      {/* Statistics cards */}
      <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        {userStats.map((item) => (
          <div
            key={item?.name}
            className="relative bg-white pt-3 px-2 sm:pt-2 sm:px-2 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className="absolute bg-indigo-500 rounded-md p-3">
                {item?.icon}
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {item?.name}
              </p>
            </dt>
            <dd className="ml-16 pb-2 flex items-baseline sm:pb-3">
              <p className="text-2xl font-semibold text-gray-900">
                {item?.stat}
              </p>
            </dd>
          </div>
        ))}
      </dl>

      {/* Charts */}
      <div className="w-full h-5/6 grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Registrations chart - same as newPostsByDate */}
        <div className="h-80 justify-center white-box">
          <Chart
            options={{
              chart: {
                id: "registrationsChart",
                locales: [fr],
                defaultLocale: "fr",
              },
              title: {
                text: t("stats.registrationsEvolution"),
                align: "center",
                margin: 18,
                style: {
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#263238",
                },
              },
              stroke: {
                curve: "straight",
              },
              legend: {
                position: "bottom",
              },
              xaxis: {
                type: "datetime",
                categories: registrationsDataset.labels,
              },
              yaxis: {
                tickAmount: 5,
                labels: {
                  formatter: (value) => Math.round(value).toString(),
                },
              },
              noData: {
                text: "Aucune données à afficher",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                  fontSize: "18px",
                  color: "#263238",
                },
              },
            }}
            series={registrationsDataset.datasets}
            type="bar"
            height="100%"
          />
        </div>

        {/* Logins chart - same as threadsByType */}
        <div className="h-80 justify-center white-box">
          <Chart
            options={{
              chart: {
                id: "loginsChart",
                locales: [fr],
                defaultLocale: "fr",
              },
              title: {
                text: t("stats.loginsByPlatform"),
                align: "center",
                margin: 18,
                style: {
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#263238",
                },
              },
              stroke: {
                curve: "smooth",
              },
              legend: {
                position: "bottom",
              },
              xaxis: {
                type: "datetime",
              },
              yaxis: {
                tickAmount: 5,
                labels: {
                  formatter: (value) => Math.round(value).toString(),
                },
              },
              noData: {
                text: "Aucune données à afficher",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                  fontSize: "18px",
                  color: "#263238",
                },
              },
            }}
            series={loginsByPlatform.datasets}
            type="line"
            height="100%"
            width="100%"
          />
        </div>
      </div>
    </>
  );
}
